import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, filter, map, withLatestFrom } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import {
  NOTIFICATION_SHOW,
  BUSINESS_SETTING_CREATE,
  BUSINESS_SETTING_CREATE_COMPLETED,
  BUSINESS_SETTING_EDIT_MODE,
  BUSINESS_SETTING_FIND,
  BUSINESS_SETTING_FIND_SUCCESS,
  BUSINESS_SETTING_RESET_ERROR,
  BUSINESS_SETTING_UPDATE_ID,
  BUSINESS_SETTING_UPDATE_LOADING,
  BUSINESS_SETTING_UPDATE_VIEW,
  SERVICE_FAILURE,
  BUSINESS_SETTING_CREATE_VIEW,
  BUSINESS_SETTING_UPDATE_COMPLETED,
} from '@store/actions';
import { ConfiguracionGeneral, ConfiguracionGeneralForm } from '@core/models';
import { BusinessSettingService } from '@core/services';
import { selectBusinessSettingForm } from '@store/selectors';

@Injectable()
export class BusinessSettingEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private businessSettingService: BusinessSettingService
  ) {}

  creationComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BUSINESS_SETTING_CREATE_COMPLETED),
      tap(() => {
        this.businessSettingService.creationCompleted();
      })
    ),
    { dispatch: false }
  )

  updateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BUSINESS_SETTING_UPDATE_COMPLETED),
      tap(() => {
        this.businessSettingService.updateCompleted();
      })
    ),
    { dispatch: false }
  )

  createForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BUSINESS_SETTING_CREATE_VIEW),
      mergeMap(() => {
        return [
          BUSINESS_SETTING_EDIT_MODE({ editMode: true, isEditing: false }),
        ];
      }),
    )
  );

  editForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BUSINESS_SETTING_UPDATE_VIEW),
      mergeMap(({ businessSettingId }) => {
        return [
          BUSINESS_SETTING_UPDATE_ID({ businessSettingId }),
          BUSINESS_SETTING_EDIT_MODE({ editMode: true, isEditing: true }),
        ];
      }),
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BUSINESS_SETTING_FIND),
      filter((action) => action.id !== null),
      mergeMap(({ id }) => {
        this.store.dispatch(BUSINESS_SETTING_UPDATE_LOADING({  loading: true }));
        this.businessSettingService.cancelPendingRequests();

        return this.businessSettingService.getBusinessSettings({ id }).pipe(
          mergeMap(({ data }) => {
            const [businessSetting] = data as ConfiguracionGeneral[];

            return [
              BUSINESS_SETTING_FIND_SUCCESS({ businessSetting }),
              BUSINESS_SETTING_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  BUSINESS_SETTING_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BUSINESS_SETTING_CREATE),
      withLatestFrom(
        this.store.select(selectBusinessSettingForm),
      ),
      filter(([_, businessSetting]) =>  !!businessSetting),
      map(([_, businessSetting]) => {
        return { businessSetting: businessSetting as ConfiguracionGeneralForm }
      }),
      mergeMap(({ businessSetting }) => {
        this.store.dispatch(BUSINESS_SETTING_UPDATE_LOADING({  loading: true }));

        return this.businessSettingService.create(businessSetting).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Business Setting Successfully Update' } }),
              BUSINESS_SETTING_UPDATE_LOADING({ loading: false }),
              BUSINESS_SETTING_CREATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  BUSINESS_SETTING_RESET_ERROR(),
                  BUSINESS_SETTING_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );
}
